// @ts-nocheck
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ReactCamera from "pages/ ScanTheRow/ReactCamera/ReactCamera";
import {
  buildTicketCaptureFileName,
  convertCaptureDataUriToFile,
  pickValidTicketImageFiles,
} from "./ticketMediaUtils";

const TicketImageField = ({
  isDisabled = false,
  errorMessage = "",
  maxCount = 2,
  selectedCount = 0,
  newFiles = [],
  existingItems = [],
  onAddFiles,
  onRemoveNewFile,
  onRemoveExistingItem,
}) => {
  const { t } = useTranslation("ticketManagement");
  const { addToast } = useToasts();
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [isOpenCaptureModal, setIsOpenCaptureModal] = useState(false);

  const remainingSlots = Math.max(0, maxCount - selectedCount);
  const canAddMore = remainingSlots > 0 && !isDisabled;

  const toastMaxImages = () => {
    addToast(t("toast.createMaxImages"), {
      appearance: "warning",
      autoDismiss: true,
    });
  };

  const toastInvalidFormat = () => {
    addToast(t("toast.createImageFormat"), {
      appearance: "warning",
      autoDismiss: true,
    });
  };

  const toastInvalidSize = () => {
    addToast(t("toast.createImageSize"), {
      appearance: "warning",
      autoDismiss: true,
    });
  };

  const appendValidatedFiles = rawFiles => {
    const validFiles = pickValidTicketImageFiles({
      rawFiles,
      remainingSlots,
      onInvalidFormat: toastInvalidFormat,
      onInvalidSize: toastInvalidSize,
      onMaxExceeded: toastMaxImages,
    });

    if (!validFiles?.length) {
      return;
    }

    onAddFiles(validFiles);
  };

  const handleChangeFiles = event => {
    appendValidatedFiles(event?.target?.files || []);

    if (event?.target) {
      event.target.value = "";
    }
  };

  const handleCaptureImage = dataUri => {
    const file = convertCaptureDataUriToFile(
      dataUri,
      buildTicketCaptureFileName()
    );
    appendValidatedFiles([file]);
    setIsOpenCaptureModal(false);
  };

  const handleOpenCaptureModal = () => {
    if (!canAddMore) {
      toastMaxImages();
      return;
    }

    setIsOpenCaptureModal(true);
  };

  const handleCaptureFromCamera = () => {
    webcamRef.current?.captureImage();
  };

  return (
    <>
      <div
        className={`border rounded bg-white overflow-hidden${errorMessage ? " border-danger" : ""}`}
      >
        <div className="d-flex border-bottom">
          <button
            type="button"
            className="flex-fill btn btn-light border-0 rounded-0 py-3"
            disabled={!canAddMore}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fas fa-upload me-2 text-primary" />
            Tải ảnh lên
          </button>
          <button
            type="button"
            className="flex-fill btn btn-light border-0 rounded-0 py-3 border-start"
            disabled={!canAddMore}
            onClick={handleOpenCaptureModal}
          >
            <i className="bx bx-camera me-2 text-info" />
            Chụp ảnh
          </button>
        </div>
        <div className="px-3 py-2 border-bottom text-muted small text-center">
          Định dạng: .png, .jpg, .jpeg — tối đa 5MB/ảnh
        </div>
        <div className="px-3 py-2 border-bottom">
          <span className="text-muted">{`Đã chọn ${selectedCount}/${maxCount} ảnh`}</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="d-none"
          onChange={handleChangeFiles}
        />
        {!!(existingItems.length || newFiles.length) && (
          <div className="border-top bg-light p-2 px-3">
            {existingItems.map((item, idx) => (
              <div
                key={`existing-${item.id}`}
                className={`d-flex align-items-center justify-content-between py-1${idx < existingItems.length - 1 || newFiles.length > 0 ? " border-bottom border-dashed" : ""}`}
              >
                <span className="text-truncate pe-2 text-dark" title={item.name}>
                  {item.name}
                </span>
                <Button
                  type="button"
                  size="sm"
                  color="link"
                  className="ticket-image-delete-btn p-0"
                  onClick={() => onRemoveExistingItem?.(item.id)}
                  disabled={isDisabled}
                  title="Xóa ảnh"
                >
                  <i className="fas fa-trash-alt" />
                </Button>
              </div>
            ))}
            {newFiles.map((file, idx) => (
              <div
                key={`${file.name}-${idx}`}
                className={`d-flex align-items-center justify-content-between py-1${idx < newFiles.length - 1 ? " border-bottom border-dashed" : ""}`}
              >
                <span className="text-truncate text-dark pe-2" title={file.name}>
                  {file.name}
                </span>
                <Button
                  type="button"
                  size="sm"
                  color="link"
                  className="ticket-image-delete-btn p-0"
                  onClick={() => onRemoveNewFile(idx)}
                  disabled={isDisabled}
                  title="Xóa ảnh"
                >
                  <i className="fas fa-trash-alt" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      {errorMessage ? (
        <small className="text-danger d-block mt-1">{errorMessage}</small>
      ) : null}

      <Modal
        isOpen={isOpenCaptureModal}
        toggle={() => setIsOpenCaptureModal(false)}
        centered
        size="lg"
        trapFocus={false}
        zIndex={2060}
      >
        <ModalHeader toggle={() => setIsOpenCaptureModal(false)}>
          Chụp ảnh ticket
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">
            Chọn camera để xem khung hình, căn góc chụp rồi bấm &quot;Chụp và thêm&quot;.
          </p>
          {isOpenCaptureModal ? (
            <ReactCamera
              ref={webcamRef}
              isAllowCapture={canAddMore}
              inlinePreview
              preferBackCameraOnMobile
              preserveCaptureAspectRatio
              handleCaptureImage={handleCaptureImage}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setIsOpenCaptureModal(false)}>
            Đóng
          </Button>
          <Button color="primary" onClick={handleCaptureFromCamera} disabled={!canAddMore}>
            Chụp và thêm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TicketImageField;
